import type { Dispatch, SetStateAction } from 'react';
import { DeprecatedLink as Link, Placeholder, Text } from '@lmn/design-system';
import styled, { css } from 'styled-components';
import { IconChecking, IconCircleInfoLinear } from '@/app/common/icons/libraryIcons';
import { useTransportChangeFilters } from '@/app/components/hotel-detail/changeTransport/ChangeTransportFiltersProvider';
import useTransportSorting from '@/app/components/hotel-detail/hooks/useTransportSorting';
import type { TranslationFunction } from '@/app/components/translations/Translation';
import { useTranslation } from '@/app/components/translations/Translation';
import { useCurrentFilterStatus } from '@/app/components/hotel-detail/CurrentFilterStatusProvider';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';
import { rankingCriteriaCopy } from '@/app/components/hotel-detail/changeTransport/rankingCriteriaCopy.ts';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import { IconExternalLink } from '../common/icons';

type ChangeTransportSortingProps = {
  modalView?: boolean;
  closingHandler?: Dispatch<SetStateAction<boolean>>;
} & React.HTMLAttributes<HTMLElement>;

export const sortOptionsMap: { [key in SortFilterOption]: TransportSortOption } = {
  POPULAR: 'BERTOLI',
  PRICE: 'PRICE_TRANSPORT',
  FASTEST: 'FASTEST_TRANSPORT',
};

export const sortEventOriginMap: { [key in TransportSortOption]: SortFilterEventOrigin } = {
  BERTOLI: 'POPULAR_SORTING',
  PRICE_TRANSPORT: 'PRICE_SORTING',
  FASTEST_TRANSPORT: 'FASTEST_SORTING',
};

export function getSortFilter(sortOption: TransportSortOption, sortFilter: SortFilter[]) {
  return sortFilter.map((filterData) =>
    sortOptionsMap[filterData.option] === sortOption
      ? { ...filterData, selected: true }
      : { ...filterData, selected: false }
  );
}

// #region styled components

const SortOption = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1 1 0;
  padding: 8px;
`;

const RecommendedSortingLinkContainer = styled.div`
  display: flex;
  font-size: ${(p) => p.theme.commons.font.sizes.base};
  align-items: center;
  padding: 16px;
`;

// #endregion

export function getSortOptionName({
  sortOption,
  t,
  isTrain,
}: {
  sortOption: TransportSortOption;
  t: TranslationFunction;
  isTrain: boolean;
}) {
  return sortOption === 'BERTOLI'
    ? t('vacation.oss.change_transport.sort.best')
    : sortOption === 'PRICE_TRANSPORT'
      ? t(
          isTrain ? 'vacation.trainavailability.sort.sorttype.price' : 'vacation.flightavailability.sort.sorttype.price'
        )
      : sortOption === 'FASTEST_TRANSPORT'
        ? t(
            isTrain
              ? 'vacation.trainavailability.sort.sorttype.fastest'
              : 'vacation.flightavailability.sort.sorttype.fastest'
          )
        : 'UNKNOWN';
}

export default function ChangeTransportSorting(props: ChangeTransportSortingProps) {
  const { modalView, closingHandler } = props;
  const { data: sortData } = useTransportSorting();
  const { currentFiltersStatus = undefined } = useCurrentFilterStatus();
  const { changeFilter } = useTransportChangeFilters();
  const { isTrain } = useUrlSearchParams();
  const { t } = useTranslation();
  const { sendCuriosityClickEvent } = useCuriosity();
  const { localization } = usePageWrapperData();
  const recommendedSortingInfoUrl = localization.rankingLink;
  const rankingCriteria = rankingCriteriaCopy(t);

  async function onSortChange(sortOption: TransportSortOption) {
    if (!currentFiltersStatus?.sortOptions) return;
    sendCuriosityClickEvent({
      event_name: 'click_transport_sorting',
      event_value:
        sortOption === 'PRICE_TRANSPORT' ? 'cheapest' : sortOption === 'FASTEST_TRANSPORT' ? 'fastest' : 'recommended',
    });
    const newState = { sortOptions: getSortFilter(sortOption, currentFiltersStatus.sortOptions) };
    const event: SortFilterEvent = { type: 'SORT', origin: sortEventOriginMap[sortOption] };
    changeFilter({ newState, event });
    if (modalView && closingHandler) {
      closingHandler(false);
    }
  }

  const selectedSortFilterOption = currentFiltersStatus
    ? currentFiltersStatus.sortOptions?.find((option) => option.selected)?.option
    : null;
  const selectedSortOption = selectedSortFilterOption ? sortOptionsMap[selectedSortFilterOption] : null;
  return modalView ? (
    <div
      css={css`
        background-color: ${(p) => p.theme.commons.colors.gray50};
      `}
    >
      <div
        css={css`
          border-radius: 8px;
          overflow: hidden;
        `}
      >
        {sortData
          ? sortData.map(({ sortOption }) => (
              <SortOption
                role="button"
                tabIndex={0}
                key={sortOption}
                css={css`
                  flex-direction: row;
                  justify-content: space-between;
                  column-gap: 8px;
                  cursor: pointer;
                  background-color: ${(p) => p.theme.commons.colors.gray0};
                  padding: 12px 16px;
                  line-height: 24px;
                  &:not(:last-child) {
                    border-bottom: 1px solid ${(p) => p.theme.commons.colors.gray100};
                  }
                `}
                onClick={() => onSortChange(sortOption)}
                onKeyDown={() => onSortChange(sortOption)}
              >
                <div
                  css={`
                    display: flex;
                    flex-direction: column;
                  `}
                >
                  <div>{getSortOptionName({ sortOption, t, isTrain })}</div>
                </div>
                {selectedSortOption === sortOption && (
                  <IconChecking
                    css={css`
                      color: ${(p) => p.theme.commons.colors.primary};
                      min-width: 20px;
                      min-height: 20px;
                    `}
                  />
                )}
              </SortOption>
            ))
          : null}
        <RecommendedSortingLinkContainer>
          <span
            css={`
              margin-right: 0.5ch;
            `}
          >
            {rankingCriteria.lead}
          </span>
          <Link
            variant="secondary"
            appearance="underlined"
            role="button"
            tabIndex={0}
            href={recommendedSortingInfoUrl}
            target="_blank"
            rel="noreferrer"
            css={css`
              margin-right: 0.5ch;
              ${(p) => p.theme.kallisto.mixins.font('BodySmall')};
            `}
          >
            {rankingCriteria.link}
          </Link>
          {rankingCriteria.tail && (
            <span
              css={`
                margin-right: 0.5ch;
              `}
            >
              {rankingCriteria.tail}
            </span>
          )}
          <IconExternalLink color="gray400" />
        </RecommendedSortingLinkContainer>
      </div>
    </div>
  ) : (
    <div
      css={css`
        display: flex;
        justify-content: space-around;
        background-color: ${({ theme }) => theme.commons.colors.gray0};
        border-radius: 8px;
        overflow: hidden; // This is to keep the border-bottom of the children inside the rounded border
        box-shadow: ${({ theme }) => theme.kallisto.shadow.mediumDownShadow};
        font-size: ${(p) => p.theme.commons.font.sizes.base};
      `}
      {...props}
    >
      {!sortData || !sortData.length ? <SortPlaceholders /> : null}
      {sortData
        ? sortData.map(({ sortOption, priceIncrement }) => (
            <SortOption
              role="button"
              tabIndex={0}
              key={sortOption}
              css={css`
                cursor: pointer;

                &:not(:last-child) {
                  border-right: 1px solid ${(p) => p.theme.commons.colors.gray100};
                }

                &:hover {
                  background-color: ${(p) => p.theme.commons.colors.primary50};
                }

                ${
                  selectedSortOption === sortOption
                    ? css`
                        color: ${(p) => p.theme.commons.colors.primary};
                        border-bottom: 4px solid ${(p) => p.theme.commons.colors.primary};
                      `
                    : css`
                        border-bottom: 2px;
                      `
                }

                ${
                  sortOption === 'BERTOLI'
                    ? css`
                        gap: 2px;
                      `
                    : css`
                        gap: 4px;
                      `
                }
              `}
              onClick={() => onSortChange(sortOption)}
              onKeyDown={() => onSortChange(sortOption)}
            >
              <div
                css={css`
                  display: flex;
                `}
              >
                <Text variant="BodySmall" weight="bold">
                  {getSortOptionName({ sortOption, t, isTrain })}
                </Text>
                {sortOption === 'BERTOLI' && (
                  <Link
                    variant="secondary"
                    appearance="underlined"
                    role="button"
                    tabIndex={0}
                    href={recommendedSortingInfoUrl}
                    target="_blank"
                    rel="noreferrer"
                    css={`
                      margin: 2px 0px 0px 4px;
                    `}
                  >
                    <IconCircleInfoLinear
                      css={css`
                        ${
                          selectedSortOption === sortOption &&
                          css`
                            color: ${(p) => p.theme.commons.colors.primary};
                            &:hover {
                              color: ${(p) => p.theme.commons.colors.primary800};
                            }
                          `
                        }
                        min-width: 16px;
                        min-height: 16px;
                      `}
                    />
                  </Link>
                )}
              </div>
              <div>
                {priceIncrement < 0
                  ? formatPrice({ price: priceIncrement, localization })
                  : `+${formatPrice({ price: priceIncrement, localization })}`}
              </div>
            </SortOption>
          ))
        : null}
    </div>
  );
}

function SortPlaceholders() {
  return (
    <>
      <SortOptionPlaceholder />
      <SortOptionPlaceholder />
      <SortOptionPlaceholder />
    </>
  );
}

const SortOptionPlaceholder = () => (
  <div
    css={`
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 8px;
      gap: 2px;
    `}
  >
    <Placeholder height="16px" width="80px" />
    <Placeholder height="14px" width="40px" />
  </div>
);
